/**
 * Carteira Vision Tax — clientes que a equipe da Vision acompanha (carnê-leão,
 * recibos de PF, notas de PJ). Mostra só esses clientes, com as pendências,
 * pra tela ficar menos poluída que o Operacional (que lista todo mundo).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, CardContent, Button, TextField, InputAdornment,
  Chip, CircularProgress, Alert, Pagination, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Switch, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import operacionalService from '../../services/operacionalService';
import clinicaService from '../../services/clinicaService';

const ORANGE = '#F97316';
const GREEN = '#10B981';

const TIPO_LABEL = { PF: 'Pessoa Física', PJ: 'Pessoa Jurídica', HIBRIDO: 'PF + PJ' };

const Pendencia = ({ label, valor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{label}</Typography>
    <Chip
      label={valor > 0 ? valor : 'em dia'}
      size="small"
      sx={{
        height: 20, fontSize: 11, fontWeight: 700,
        bgcolor: valor > 0 ? 'rgba(249,115,22,0.12)' : 'rgba(16,185,129,0.12)',
        color: valor > 0 ? ORANGE : GREEN,
      }}
    />
  </Box>
);

const ClienteCard = ({ clinica, onAbrir, onRemover }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', height: '100%',
      display: 'flex', flexDirection: 'column',
      transition: 'box-shadow 0.18s, border-color 0.18s',
      '&:hover': { boxShadow: '0 8px 28px rgba(249,115,22,0.12)', borderColor: 'rgba(249,115,22,0.3)' },
    }}
  >
    <Box sx={{ height: 4, background: `linear-gradient(90deg, ${ORANGE} 0%, #FB923C 100%)` }} />
    <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: '#0F172A' }} noWrap>{clinica.nome}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap>{clinica.cnpj || clinica.cpf || 'Sem CNPJ/CPF'}</Typography>
        </Box>
        <Tooltip title="Tirar da carteira da Vision">
          <IconButton size="small" onClick={() => onRemover(clinica)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
            <PersonRemoveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Chip label={TIPO_LABEL[clinica.tipoPessoa] || clinica.tipoPessoa} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        <Chip label={clinica.plano} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        {!clinica.ativo && <Chip label="Inativo" size="small" color="error" sx={{ height: 20, fontSize: 11 }} />}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1, borderTop: '1px dashed #E2E8F0', borderBottom: '1px dashed #E2E8F0' }}>
        <Pendencia label="Recibos PF pendentes" valor={clinica.recibosPendentes} />
        <Pendencia label="Notas PJ pendentes" valor={clinica.notasPendentes} />
      </Box>

      <Button
        variant="contained" disableElevation startIcon={<VisibilityIcon />} onClick={() => onAbrir(clinica)}
        sx={{ mt: 'auto', bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
      >
        Abrir cliente
      </Button>
    </CardContent>
  </Card>
);

/* Janela pra escolher quem entra na carteira — busca em todas as clínicas */
const AdicionarClientesDialog = ({ open, onClose }) => {
  const [clinicas, setClinicas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [page, setPage] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [salvandoId, setSalvandoId] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true); setErro('');
      const d = await clinicaService.listarClinicasOperacional({ page, limit: 8, search });
      setClinicas(d.clinicas || []);
      setTotalPaginas(d.totalPaginas || 1);
    } catch { setErro('Erro ao buscar clínicas'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { if (open) carregar(); }, [open, carregar]);

  const alternar = async (clinica) => {
    setSalvandoId(clinica.id);
    try {
      await operacionalService.definirVisionTax(clinica.id, !clinica.visionTax);
      setClinicas((lista) => lista.map((c) => (c.id === clinica.id ? { ...c, visionTax: !clinica.visionTax } : c)));
    } catch { setErro('Não foi possível atualizar esse cliente.'); }
    finally { setSalvandoId(null); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontWeight={700}>Adicionar clientes da Vision</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Ligue a chave dos clientes que a Vision Tax acompanha. Eles passam a aparecer na carteira.
        </Typography>
        <TextField
          fullWidth size="small" placeholder="Buscar por nome, CPF ou CNPJ..." value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); setSearch(searchInput.trim()); } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2 }}
        />
        {erro && <Alert severity="error" sx={{ mb: 1.5 }}>{erro}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: ORANGE }} /></Box>
        ) : clinicas.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ py: 2, textAlign: 'center' }}>Nenhuma clínica encontrada.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {clinicas.map((c) => (
              <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 1, borderBottom: '1px solid #F1F5F9' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 13.5 }} noWrap>{c.nome}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: '#94A3B8' }} noWrap>
                    {TIPO_LABEL[c.tipoPessoa] || c.tipoPessoa} · {c.cnpj || c.cpf || 'sem documento'}
                  </Typography>
                </Box>
                <Switch
                  checked={!!c.visionTax} disabled={salvandoId === c.id} onChange={() => alternar(c)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ORANGE }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ORANGE } }}
                />
              </Box>
            ))}
          </Box>
        )}
        {totalPaginas > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPaginas} page={page} onChange={(e, v) => setPage(v)} size="small" />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" disableElevation sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, textTransform: 'none', fontWeight: 700 }}>
          Concluir
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const VisionTax = () => {
  const navigate = useNavigate();
  const [clinicas, setClinicas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setLoading(true); setErro('');
      const d = await operacionalService.listarVisionTax({ search });
      setClinicas(d.clinicas || []);
    } catch { setErro('Erro ao carregar a carteira Vision Tax'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { carregar(); }, [carregar]);

  const remover = async (clinica) => {
    if (!window.confirm(`Tirar "${clinica.nome}" da carteira da Vision?\nO cliente continua no sistema, só deixa de aparecer aqui.`)) return;
    try {
      await operacionalService.definirVisionTax(clinica.id, false);
      setClinicas((lista) => lista.filter((c) => c.id !== clinica.id));
    } catch { setErro('Não foi possível remover esse cliente.'); }
  };

  const totalRecibos = clinicas.reduce((s, c) => s + (c.recibosPendentes || 0), 0);
  const totalNotas = clinicas.reduce((s, c) => s + (c.notasPendentes || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A' }}>Vision Tax</Typography>
          <Typography variant="body2" color="text.secondary">Clientes que a equipe acompanha</Typography>
        </Box>
        <Button
          variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setDialogAberto(true)}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
        >
          Adicionar clientes
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
        <TextField
          size="small" placeholder="Buscar na carteira..." value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput.trim()); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 260 }}
        />
        <Chip label={`${clinicas.length} cliente${clinicas.length === 1 ? '' : 's'}`} sx={{ fontWeight: 700 }} />
        <Chip label={`${totalRecibos} recibo${totalRecibos === 1 ? '' : 's'} PF pendente${totalRecibos === 1 ? '' : 's'}`} sx={{ fontWeight: 700, bgcolor: totalRecibos ? 'rgba(249,115,22,0.12)' : undefined, color: totalRecibos ? ORANGE : undefined }} />
        <Chip label={`${totalNotas} nota${totalNotas === 1 ? '' : 's'} PJ pendente${totalNotas === 1 ? '' : 's'}`} sx={{ fontWeight: 700, bgcolor: totalNotas ? 'rgba(249,115,22,0.12)' : undefined, color: totalNotas ? ORANGE : undefined }} />
      </Box>

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
      ) : clinicas.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '1.5px dashed #E2E8F0', borderRadius: 3 }}>
          <Typography sx={{ fontWeight: 700, color: '#475569' }}>
            {search ? 'Nenhum cliente encontrado na carteira.' : 'Nenhum cliente na carteira ainda.'}
          </Typography>
          {!search && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Clique em "Adicionar clientes" e escolha quem a Vision Tax acompanha.
            </Typography>
          )}
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {clinicas.map((c) => (
            <Grid item xs={12} sm={6} lg={4} key={c.id}>
              <ClienteCard clinica={c} onAbrir={(cl) => navigate(`/admin/operacional/clinica/${cl.id}`)} onRemover={remover} />
            </Grid>
          ))}
        </Grid>
      )}

      <AdicionarClientesDialog open={dialogAberto} onClose={() => { setDialogAberto(false); carregar(); }} />
    </Box>
  );
};

export default VisionTax;
